<template>
  <div class="w-full">
    <div class="topWrapper primaryFont">
      <h1>ESCRITORIO</h1>
      <div class="flex justify-content-end gap-4 mb-2">
        <ItemsAlertBox
          v-if="offlineActuations.offlineCount && showAlertOffline"
          :pendingCount="offlineActuations.offlineCount"
          title="Actuaciones sin conexión"
          message="Tenes actuaciones sin conexión para revisar"
          buttonLabel="Ver"
          :onClick="handelOfflineNotification"
        >
          <template #icon>
            <BellDot :size="20" color="white" />
          </template>
        </ItemsAlertBox>
        <div v-if="showDropdown" class="dropdown-wrapper">
          <label for="dropDependencia">Seleccione la Dependencia</label>
          <Dropdown
            id="dropDependencia"
            v-model="dependenciaSelected"
            :options="dependenciaList"
            filter
            optionLabel="dependencia"
            optionValue="id"
            placeholder="Seleccione una dependencia..."
            showClear
            style="border-radius: 6px"
            class="custom-focus"
          />
        </div>
      </div>
    </div>
    <div class="layout-wrapper">
      <div class="left-column">
        <div class="column" v-for="(columnItems, colIndex) in columnasStats" :key="colIndex">
          <StatsRadial
            v-for="(item, index) in columnItems"
            :key="index"
            :title="item.label"
            :series="[item.enCurso, item.vencidas]"
            :labels="labels"
            :titleSize="item.titleSize || '32px'"
          />
        </div>
      </div>
      <div class="right-column">
        <StatsBar
          style="width: 883px"
          title="UFIS Delitos"
          :series="series"
          :categories="categories"
          :loading="loading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";

import { useStoreCompat } from "@/composables/useStoreCompat";
import { useUserAccess } from "@/composables/useUserAccess.js";
import { useProsecutor } from "@/composables/DenunciaFormDatosGenerales/useProsecutor";
import { useRouterCompat } from "@/composables/useRouterCompat.js";

import { useEstadisticasStore } from "@/store/Pinia/useEstadisticasStore";
import StatsBar from "@/components/Dashboard/StatsBar.vue";
import StatsRadial from "@/components/Dashboard/StatsRadial.vue";
import { storeToRefs } from "pinia";
import { useOfflineActuationsStore } from "@/store/Pinia/useOfflineActuations";
import ItemsAlertBox from "../ItemsAlertBox.vue";
import { BellDot } from "lucide-vue";

export default {
  name: "DashboardUFIS",
  components: {
    StatsBar,
    StatsRadial,
    ItemsAlertBox,
    BellDot,
  },
  setup() {
    const store = useStoreCompat();
    const { esTipoUsuario } = useUserAccess();
    const { prosecutorUserTypes } = useProsecutor();
    const offlineActuations = useOfflineActuationsStore();
    const { router } = useRouterCompat();

    const series = ref([]);
    const categories = ref([]);

    const usuario = ref(store.state.user);

    const estadisticasStore = useEstadisticasStore();
    const { gralStats, dependenciaSelected, UFIStats, loading, ordenUFIs } =
      storeToRefs(estadisticasStore);

    const columnasStats = computed(() => {
      const items = Object.values(gralStats.value);
      const col1 = [];
      const col2 = [];

      items.forEach((item, index) => {
        if (index % 2 === 0) col1.push(item);
        else col2.push(item);
      });
      return [col1, col2];
    });
    const labels = ["En curso", "Vencidas"];

    const dependenciaList = computed(() => store.getters["siis/getDependecias"]);

    const showDropdown = computed(() => {
      const types = [
        "Super User",
        "User Tester",
        "Super Usuario de E3",
        "Dpto. Asuntos Judiciales D-5",
        "Dpto.Criminalistica",
        "Lab. Informatico Forense",
        "Control de Gestion",
        "Dirección de Información para el Delito D-2",
        "Dpto. Planeamiento",
        "UsuarioDepartamental",
        ...prosecutorUserTypes.value,
      ];
      return esTipoUsuario(types, usuario.value);
    });

    const showAlertOffline = computed(() => {
      return esTipoUsuario(
        ["Super User", "Jefe de Dependencia", "Jefe de Sumario", "Oficial Sumariante"],
        usuario.value
      );
    });

    const handelOfflineNotification = () => {
      router.push({ path: "/actuaciones/list", query: { tabIndex: 2 } });
    };

    watch(
      UFIStats,
      () => {
        const completeStats = ordenUFIs.value.map((nombre) => {
          const actual = UFIStats.value.find((item) => item.label === nombre);
          return {
            tipoSumarioID: nombre,
            enCurso: actual?.enCurso || 0,
            vencidas: actual?.vencidas || 0,
            // finalizadas: actual?.finalizadas || 0,
          };
        });
        categories.value = completeStats?.map((d) => d.tipoSumarioID);
        const enCurso = completeStats.map((d) => d.enCurso || 0);
        const vencidas = completeStats.map((d) => d.vencidas || 0);
        // const finalizadas = completeStats.map(d => d.finalizadas || 0)
        series.value = [
          { name: "En curso", data: enCurso },
          { name: "Vencidas", data: vencidas },
          // { name: 'Finalizadas', data: finalizadas }
        ];
      },
      { immediate: true }
    );

    onMounted(async () => {
      await estadisticasStore.fetchEstadisticas();
      if (
        esTipoUsuario(
          ["Jefe de Dependencia", "Jefe de Sumario", "Oficial Sumariante"],
          usuario.value
        )
      ) {
        dependenciaSelected.value = usuario.value.userdata.dependenciaID;
      }

      await offlineActuations.getOfflineActuations();
      store.dispatch("siis/setDependencias");
    });

    return {
      dependenciaList,
      usuario,
      showDropdown,
      labels,
      columnasStats,
      dependenciaSelected,
      series,
      loading,
      categories,
      offlineActuations,
      handelOfflineNotification,
      showAlertOffline,
    };
  },
};
</script>
<style scoped>
.layout-wrapper {
  width: 100%;
  display: flex;
  gap: 3rem;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
}

@media (max-width: 1280px) {
  .layout-wrapper {
    justify-content: center;
  }
}

.left-column {
  display: flex;
  gap: 4rem;
  flex-direction: row;
  flex-wrap: wrap;
  max-width: 600px;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 2.87rem;
}

.right-column {
  min-width: 500px;
}

.dropdown-wrapper {
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.topWrapper {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: start;
  margin-top: 20px;
}

.topWrapper h1 {
  font-weight: 700;
  margin: 0;
}

.custom-focus:focus-within {
  border: 1.5px solid #3758f9;
}

.custom-focus {
  font-family: "IBM Plex Sans", sans-serif;
}
</style>
